import type { QueryClient } from '@tanstack/react-query';

/**
 * The query keys whose answer does not depend on who is signed in.
 *
 * <h2>Why an exception list exists at all</h2>
 *
 * Signing in and signing out both drop the cache, and that is right for almost everything:
 * whatever was in it was fetched as nobody, or as somebody else, and showing one account's
 * rows under another account's name is the failure that rule exists to prevent.
 *
 * <p>One answer is not like that. `['activation-token', token]` is `POST /activate/check`,
 * which is a public endpoint addressed by the token in the URL. It names no account,
 * reads nothing tenant-scoped, and returns the same answer to everybody who holds the
 * link — so there is no account for it to be stale FOR.
 *
 * <h2>And keeping it is not an optimisation, it is `AC-63`</h2>
 *
 * Link validity outranks the session, which it can only do if the link's authoritative
 * answer survives a session change. Dropped, the page falls back to its loader and asks
 * again — so an invitation already known to be dead read:
 *
 * <pre>
 *   INVALID  ->  LOADING  ->  INVALID
 * </pre>
 *
 * which is the oscillation `AC-63` forbids, one layer down from the one it was written
 * about, and it re-ran the check on every login and every logout — the request loop the
 * precedence work was asked not to create.
 *
 * <h2>The bar for adding to this list</h2>
 *
 * The endpoint must be public, addressed by something in the URL rather than by the
 * session, and give the same answer to every caller. Anything a tenant can see is not a
 * candidate, however convenient: the cost of being wrong here is one customer's data on
 * another customer's screen.
 */
const SESSION_INDEPENDENT_KEYS: readonly string[] = ['activation-token'];

/**
 * Drops everything the session owned, and keeps what it never owned.
 *
 * <p>`removeQueries` with a predicate rather than `clear()`, which is all-or-nothing and
 * also empties the mutation cache. Mutations are deliberately left alone now: `/activate`
 * spends its link through one, and that result IS the terminal link state the precedence
 * rule says must outrank the session.
 */
export function forgetSessionScopedQueries(queryClient: QueryClient): void {
  queryClient.removeQueries({
    predicate: (query) => !SESSION_INDEPENDENT_KEYS.includes(String(query.queryKey[0])),
  });
}
